import { useState } from "react";
import { ArrowUpRight, ExternalLink, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PROJECTS = [
  {
    id: "mma-gym",
    title: "MMA Gym Website",
    niche: "Concept Build",
    status: "Concept",
    image: "https://images.unsplash.com/photo-1758778932827-52b7e4799fbf",
    tags: ["Class Schedule", "Free Trial CTA", "Mobile-first"],
    description:
      "A high-impact MMA gym site designed around one thing: getting visitors to book a free trial. Above-the-fold offer, scannable class schedule, and one-tap booking on mobile.",
    highlights: [
      "Free trial signup as the primary CTA",
      "Clear schedule + program breakdown",
      "Built for cold ad and Instagram traffic",
    ],
  },
  {
    id: "auto-detailing",
    title: "Auto Detailing Website",
    niche: "Concept Build",
    status: "Concept",
    image: "https://images.pexels.com/photos/3892898/pexels-photo-3892898.jpeg",
    tags: ["Quote Form", "Service Packages", "Gallery"],
    description:
      "Premium feel for a premium service. Clean package pricing, before/after style gallery, and a short quote form that captures lead details fast.",
    highlights: [
      "Service package pricing laid out clearly",
      "Quick quote request form",
      "Gallery to showcase real work later",
    ],
  },
  {
    id: "roofing",
    title: "Roofing Landing Page",
    niche: "Concept Build",
    status: "Concept",
    image: "https://images.unsplash.com/photo-1633759593085-1eaeb724fc88",
    tags: ["Free Estimate", "Trust Badges", "Phone-first"],
    description:
      "A trust-heavy landing page built for cold ad traffic. Phone-first calls-to-action, license/insurance badges, and a short free-estimate form.",
    highlights: [
      "Phone CTA above the fold",
      "Trust badges + service area",
      "Free estimate form optimized for mobile",
    ],
  },
  {
    id: "pressure-washing",
    title: "Pressure Washing Website",
    niche: "Concept Build",
    status: "Concept",
    image: "https://images.pexels.com/photos/5652626/pexels-photo-5652626.jpeg",
    tags: ["Before/After", "Service Areas", "Instant Quote"],
    description:
      "Designed around how local homeowners actually shop. Service area map, simple booking flow, and space for before/after photos.",
    highlights: [
      "Service area + booking flow",
      "Room for before/after photos",
      "Mobile-first checkout style request",
    ],
  },
  {
    id: "landscaping",
    title: "Landscaping Website",
    niche: "Concept Build",
    status: "Concept",
    image: "https://images.unsplash.com/photo-1734303023491-db8037a21f09",
    tags: ["Portfolio", "Seasonal Offers", "Maintenance Plans"],
    description:
      "A clean site that shows seasonal work and turns one-time customers into recurring maintenance clients. Includes plan selector and a clean contact flow.",
    highlights: [
      "Seasonal portfolio layout",
      "Plan selector for maintenance",
      "Lead-capture contact form",
    ],
  },
  {
    id: "woodworking",
    title: "Custom Woodworking Client Site",
    niche: "Real Client",
    status: "In Progress",
    image: "https://images.unsplash.com/photo-1601058268499-e52658b8bb88",
    tags: ["Real Client", "Coming Soon", "In Build"],
    description:
      "A real client project currently in build for a custom woodworking business. Focused on showcasing craftsmanship, gallery work, and direct contact for custom orders.",
    highlights: [
      "Craftsmanship-first gallery layout",
      "Custom order request flow",
      "Launch coming soon",
    ],
  },
];

export default function Portfolio() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.22em] uppercase text-blue-400 font-medium">
              Selected work
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold mt-4 leading-[1.1]">
              Demo Builds &amp; <span className="italic">Client Work</span>.
            </h2>
          </div>
          <p className="text-white/55 max-w-md text-sm md:text-base">
            A mix of concept builds for the niches I love working with, plus
            real client projects in progress. Click any card to see the full
            breakdown.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map((p, idx) => (
            <button
              type="button"
              key={p.id}
              data-testid={`portfolio-card-${p.id}`}
              onClick={() => setActive(p)}
              className={`group text-left card-soft rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                idx === 0 ? "md:col-span-2 lg:col-span-2" : ""
              }`}
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#171717]">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[10px] tracking-[0.18em] uppercase text-white/80">
                  {p.niche}
                </div>
                {p.status === "In Progress" && (
                  <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-blue-500/20 backdrop-blur border border-blue-400/40 text-[10px] tracking-[0.18em] uppercase text-blue-200 font-medium">
                    Coming Soon
                  </div>
                )}
              </div>
              <div className="p-6 md:p-7">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-xl md:text-2xl text-white">
                    {p.title}
                  </h3>
                  <span className="text-white/30 group-hover:text-blue-400 transition-colors">
                    <ArrowUpRight size={20} />
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-white/60"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-2 text-sm text-blue-400 group-hover:text-blue-300">
                  View Concept
                  <ArrowUpRight size={14} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="portfolio-dialog"
          className="max-w-4xl bg-[#0F0F0F] border border-white/10 text-white p-0 overflow-hidden"
        >
          {active && (
            <>
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-black">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[10px] tracking-[0.22em] uppercase text-white/85">
                  {active.niche}
                </div>
                {active.status === "In Progress" && (
                  <div className="absolute top-4 right-16 px-2.5 py-1 rounded-full bg-blue-500/25 backdrop-blur border border-blue-400/50 text-[10px] tracking-[0.22em] uppercase text-blue-100 font-medium">
                    In Progress · Coming Soon
                  </div>
                )}
                <button
                  type="button"
                  data-testid="portfolio-dialog-close"
                  aria-label="Close preview"
                  onClick={() => setActive(null)}
                  className="absolute top-4 right-4 h-10 w-10 rounded-full bg-black/70 hover:bg-black backdrop-blur border border-white/20 hover:border-white/50 text-white flex items-center justify-center transition-all shadow-lg z-10"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-7 md:p-9">
                <DialogHeader>
                  <DialogTitle className="font-display text-3xl md:text-4xl mt-2 text-white">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className="text-white/60 mt-3 text-[15px] leading-relaxed">
                    {active.description}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-6 flex flex-wrap gap-2">
                  {active.tags.map((t) => (
                    <span
                      key={t}
                      className="text-xs px-3 py-1 rounded-full border border-white/10 text-white/70"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-7">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-medium">
                    What&apos;s in the build
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {active.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-3 text-sm text-white/75"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button
                    data-testid="portfolio-dialog-cta"
                    onClick={() => {
                      setActive(null);
                      const el = document.getElementById("contact");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-11 px-6"
                  >
                    Get a similar concept
                    <ArrowUpRight size={15} className="ml-1.5" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setActive(null)}
                    className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-full h-11 px-6"
                  >
                    Close preview
                    <ExternalLink size={14} className="ml-1.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
